/**
 * Services — 本地服务 Agent 页
 * 
 * 功能：本地生活服务搜索与预约，AI Agent 智能匹配商家
 * 服务类型：洗车/美容/维修/家政/健身/宠物/摄影/婚庆/租车/搬家/培训/医疗预约
 * 
 * 设计风格：暗夜蓝 + 萤火暖金，与 LifePage 统一的主布局风格
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/* ═══════════════════════════════════════════
   类型定义
   ═══════════════════════════════════════════ */

interface ServiceCategory {
  id: string;
  label: string;
  icon: string;
}

interface Business {
  id: string;
  name: string;
  category: string;
  rating: number;
  distance: string;
  pricePerPerson: string;
  availableSlots: string[];
  tags: string[];
}

interface AgentStep {
  id: string;
  label: string;
  icon: string;
  status: 'idle' | 'running' | 'done';
}

interface BookingInfo {
  business: Business | null;
  date: string;
  timeSlot: string;
}

/* ═══════════════════════════════════════════
   静态数据
   ═══════════════════════════════════════════ */

const SERVICE_CATEGORIES: ServiceCategory[] = [
  { id: 'car-wash', label: '洗车', icon: '🚗' },
  { id: 'beauty', label: '美容', icon: '💄' },
  { id: 'repair', label: '维修', icon: '🔧' },
  { id: 'housekeeping', label: '家政', icon: '🏠' },
  { id: 'fitness', label: '健身', icon: '💪' },
  { id: 'pet', label: '宠物', icon: '🐾' },
  { id: 'photography', label: '摄影', icon: '📷' },
  { id: 'wedding', label: '婚庆', icon: '💍' },
  { id: 'car-rental', label: '租车', icon: '🚙' },
  { id: 'moving', label: '搬家', icon: '📦' },
  { id: 'training', label: '培训', icon: '📚' },
  { id: 'medical', label: '医疗预约', icon: '🏥' },
];

const MOCK_BUSINESSES: Business[] = [
  {
    id: 'b1', name: '闪亮汽车美容中心', category: 'car-wash', rating: 4.8,
    distance: '1.2km', pricePerPerson: '¥88',
    availableSlots: ['09:00', '10:30', '14:00', '16:30'],
    tags: ['精洗', '打蜡', '内饰清洁'],
  },
  {
    id: 'b2', name: '悦己美妆工作室', category: 'beauty', rating: 4.9,
    distance: '0.8km', pricePerPerson: '¥268',
    availableSlots: ['10:00', '13:00', '15:30', '18:00'],
    tags: ['日式美甲', '美睫', '皮肤管理'],
  },
  {
    id: 'b3', name: '极速家电维修', category: 'repair', rating: 4.6,
    distance: '2.1km', pricePerPerson: '¥150',
    availableSlots: ['08:30', '11:00', '14:30', '17:00'],
    tags: ['空调维修', '水管疏通', '电路检修'],
  },
  {
    id: 'b4', name: '安居家政服务', category: 'housekeeping', rating: 4.7,
    distance: '3.0km', pricePerPerson: '¥120',
    availableSlots: ['09:00', '13:00', '15:00'],
    tags: ['深度保洁', '收纳整理', '月嫂'],
  },
  {
    id: 'b5', name: '力量怪兽健身房', category: 'fitness', rating: 4.5,
    distance: '1.5km', pricePerPerson: '¥58',
    availableSlots: ['07:00', '10:00', '14:00', '19:00'],
    tags: ['私教体验', '团体课', '器械自由'],
  },
  {
    id: 'b6', name: '萌宠乐园宠物店', category: 'pet', rating: 4.8,
    distance: '0.6km', pricePerPerson: '¥98',
    availableSlots: ['09:30', '11:30', '14:00', '16:00'],
    tags: ['宠物美容', '寄养', '洗护'],
  },
];

const AGENT_PIPELINE_STEPS: AgentStep[] = [
  { id: 'analyze', label: '解析需求', icon: '🧠', status: 'idle' },
  { id: 'match', label: '智能匹配', icon: '🎯', status: 'idle' },
  { id: 'rank', label: '排序筛选', icon: '📊', status: 'idle' },
  { id: 'schedule', label: '时段协调', icon: '📅', status: 'idle' },
];

const DATE_OPTIONS = (() => {
  const dates: { label: string; value: string }[] = [];
  const today = new Date();
  for (let i = 0; i < 7; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    const dayNames = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
    const month = d.getMonth() + 1;
    const day = d.getDate();
    const m = String(month).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    dates.push({
      label: i === 0 ? `今天 ${m}/${dd}` : i === 1 ? `明天 ${m}/${dd}` : `${dayNames[d.getDay()]} ${m}/${dd}`,
      value: d.toISOString().split('T')[0],
    });
  }
  return dates;
})();

/* ═══════════════════════════════════════════
   工具函数
   ═══════════════════════════════════════════ */

const getTimeOfDay = () => {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return '上午好';
  if (h >= 12 && h < 18) return '下午好';
  if (h >= 18 && h < 22) return '晚上好';
  return '夜深了';
};

const getGreeting = () => {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return '生活小帮手已准备就绪';
  if (h >= 12 && h < 18) return '午后时光，享受一下';
  if (h >= 18 && h < 22) return '晚安前的需求我来搞定';
  return '注意休息，明天再约';
};

/* ═══════════════════════════════════════════
   子组件
   ═══════════════════════════════════════════ */

/** 服务分类卡片 */
function ServiceCategoryCard({
  category, isSelected, onClick, index,
}: {
  category: ServiceCategory; isSelected: boolean; onClick: (id: string) => void; index: number;
}) {
  return (
    <button
      onClick={() => onClick(category.id)}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: '16px 8px', borderRadius: 16,
        background: isSelected ? 'rgba(232, 210, 138, 0.08)' : 'rgba(255,255,255,0.03)',
        border: `1px solid ${isSelected ? 'rgba(232, 210, 138, 0.25)' : 'rgba(255,255,255,0.06)'}`,
        cursor: 'pointer', textAlign: 'center',
        transition: 'all 250ms var(--ease)',
        animation: `fadeUp 0.5s ${(0.1 + index * 0.04).toFixed(2)}s var(--ease) both`,
        gap: 6,
      }}
      onMouseEnter={(e) => {
        if (!isSelected) {
          e.currentTarget.style.borderColor = 'rgba(232, 210, 138, 0.15)';
          e.currentTarget.style.background = 'rgba(232, 210, 138, 0.04)';
          e.currentTarget.style.transform = 'translateY(-2px)';
        }
      }}
      onMouseLeave={(e) => {
        if (!isSelected) {
          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
          e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
          e.currentTarget.style.transform = 'translateY(0)';
        }
      }}
    >
      <span style={{ fontSize: 22 }}>{category.icon}</span>
      <span style={{
        fontSize: 12, fontWeight: isSelected ? 500 : 400,
        color: isSelected ? 'var(--accent)' : 'var(--text-secondary)',
      }}>
        {category.label}
      </span>
    </button>
  );
}

/** 商家卡片 */
function BusinessCard({
  business, isSelected, onSelect,
}: {
  business: Business; isSelected: boolean; onSelect: (b: Business) => void;
}) {
  return (
    <button
      onClick={() => onSelect(business)}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
        padding: '18px 20px', borderRadius: 16,
        background: isSelected ? 'rgba(232, 210, 138, 0.06)' : 'rgba(255,255,255,0.03)',
        border: `1px solid ${isSelected ? 'rgba(232, 210, 138, 0.3)' : 'rgba(255,255,255,0.06)'}`,
        cursor: 'pointer', textAlign: 'left', width: '100%',
        transition: 'all 250ms var(--ease)',
        gap: 10,
      }}
      onMouseEnter={(e) => {
        if (!isSelected) {
          e.currentTarget.style.borderColor = 'rgba(232, 210, 138, 0.15)';
          e.currentTarget.style.background = 'rgba(232, 210, 138, 0.03)';
        }
      }}
      onMouseLeave={(e) => {
        if (!isSelected) {
          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
          e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
        }
      }}
    >
      {/* 名称与评分 */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <span style={{ fontSize: 15, fontWeight: 500, color: 'var(--text-primary)' }}>{business.name}</span>
        <span style={{ fontSize: 13, color: 'var(--accent)', fontWeight: 500 }}>
          ★ {business.rating}
        </span>
      </div>

      {/* 距离与价格 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {business.distance}
        </span>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          人均 {business.pricePerPerson}
        </span>
      </div>

      {/* 标签 */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {business.tags.map((tag) => (
          <span
            key={tag}
            style={{
              padding: '2px 8px', borderRadius: 8,
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.06)',
              fontSize: 11, color: 'var(--text-muted)',
            }}
          >
            {tag}
          </span>
        ))}
      </div>

      {/* 可预约时段预览 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>可约:</span>
        {business.availableSlots.slice(0, 3).map((slot) => (
          <span
            key={slot}
            style={{
              padding: '2px 7px', borderRadius: 6,
              background: 'rgba(74, 222, 128, 0.06)',
              border: '1px solid rgba(74, 222, 128, 0.12)',
              fontSize: 11, color: '#4ade80',
            }}
          >
            {slot}
          </span>
        ))}
        {business.availableSlots.length > 3 && (
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            +{business.availableSlots.length - 3}
          </span>
        )}
      </div>
    </button>
  );
}

/** Agent 工作进度指示器 */
function AgentProgress({
  steps,
}: {
  steps: AgentStep[];
}) {
  return (
    <div
      style={{
        display: 'flex', flexDirection: 'column', gap: 10,
        padding: '18px 20px', borderRadius: 16,
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.05)',
        animation: 'fadeUp 0.3s var(--ease) both',
      }}
    >
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{
          width: 6, height: 6, borderRadius: '50%',
          background: '#34d399', animation: 'gentlePulse 2s ease-in-out infinite',
        }} />
        Agent 正在为您匹配服务
      </div>
      {steps.map((step, idx) => {
        const statusColor = step.status === 'done' ? '#4ade80' : step.status === 'running' ? 'var(--accent)' : 'rgba(255,255,255,0.15)';
        const statusIcon = step.status === 'done' ? '✓' : step.status === 'running' ? '⚡' : '○';
        return (
          <div
            key={step.id}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 14px', borderRadius: 10,
              background: step.status === 'running' ? 'rgba(232, 210, 138, 0.06)' : 'rgba(255,255,255,0.02)',
              border: `1px solid ${step.status === 'running' ? 'rgba(232, 210, 138, 0.2)' : 'rgba(255,255,255,0.05)'}`,
              transition: 'all 400ms var(--ease)',
              animation: step.status === 'running' ? 'gentlePulse 1.5s ease-in-out infinite' : 'none',
            }}
          >
            <span style={{ fontSize: 16, width: 24, textAlign: 'center' }}>{step.icon}</span>
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: 13,
                color: step.status === 'running' ? 'var(--accent)' : step.status === 'done' ? '#4ade80' : 'var(--text-muted)',
                fontWeight: 500,
              }}>
                {step.label}
              </div>
            </div>
            <span style={{ fontSize: 14, color: statusColor, fontWeight: 600, width: 20, textAlign: 'center' }}>
              {statusIcon}
            </span>
            {/* 步骤连接线 */}
            {idx < steps.length - 1 && (
              <div style={{
                position: 'absolute', left: 0, right: 0, height: 1,
                background: 'rgba(255,255,255,0.04)',
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/** 预约时间选择器 */
function TimeSlotPicker({
  business, selectedDate, selectedTime, onSelectDate, onSelectTime,
}: {
  business: Business | null;
  selectedDate: string;
  selectedTime: string;
  onSelectDate: (d: string) => void;
  onSelectTime: (t: string) => void;
}) {
  const dateOpts = DATE_OPTIONS;

  return (
    <div
      style={{
        padding: '20px', borderRadius: 16,
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.06)',
        animation: 'fadeUp 0.4s 0.1s var(--ease) both',
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 16 }}>
        选择预约时间
      </div>

      {/* 日期选择 */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>日期</div>
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
          {dateOpts.map((d) => (
            <button
              key={d.value}
              onClick={() => onSelectDate(d.value)}
              style={{
                padding: '6px 12px', borderRadius: 10,
                background: selectedDate === d.value ? 'rgba(232, 210, 138, 0.1)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${selectedDate === d.value ? 'rgba(232, 210, 138, 0.25)' : 'rgba(255,255,255,0.06)'}`,
                color: selectedDate === d.value ? 'var(--accent)' : 'var(--text-secondary)',
                fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap',
                transition: 'all 200ms',
              }}
            >
              {d.label}
            </button>
          ))}
        </div>
      </div>

      {/* 时段选择 */}
      <div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>时段</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {(business?.availableSlots || ['09:00', '10:30', '14:00', '16:00', '19:00']).map((slot) => (
            <button
              key={slot}
              onClick={() => onSelectTime(slot)}
              style={{
                padding: '8px 16px', borderRadius: 10,
                background: selectedTime === slot ? 'rgba(232, 210, 138, 0.1)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${selectedTime === slot ? 'rgba(232, 210, 138, 0.25)' : 'rgba(255,255,255,0.06)'}`,
                color: selectedTime === slot ? 'var(--accent)' : 'var(--text-secondary)',
                fontSize: 13, cursor: 'pointer',
                transition: 'all 200ms',
              }}
            >
              {slot}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/** 预约确认卡片 */
function BookingConfirmation({
  booking, onConfirm, onCancel,
}: {
  booking: BookingInfo;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!booking.business || !booking.date || !booking.timeSlot) return null;

  const dateLabel = DATE_OPTIONS.find(d => d.value === booking.date)?.label || booking.date;

  return (
    <div
      style={{
        padding: '20px', borderRadius: 16,
        background: 'rgba(232, 210, 138, 0.04)',
        border: '1px solid rgba(232, 210, 138, 0.15)',
        animation: 'fadeUp 0.4s 0.2s var(--ease) both',
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 18 }}>📋</span>
        确认预约信息
      </div>

      <div style={{ display: 'grid', gap: 10, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>商家</span>
          <span style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{booking.business.name}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>服务</span>
          <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>
            {SERVICE_CATEGORIES.find(c => c.id === booking.business.category)?.label || booking.business.category}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>日期</span>
          <span style={{ fontSize: 13, color: 'var(--accent)', fontWeight: 500 }}>{dateLabel}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>时段</span>
          <span style={{ fontSize: 13, color: 'var(--accent)', fontWeight: 500 }}>{booking.timeSlot}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>人均价格</span>
          <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>{booking.business.pricePerPerson}</span>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <button
          onClick={onConfirm}
          style={{
            flex: 1, padding: '12px 20px', borderRadius: 12,
            background: 'linear-gradient(135deg, var(--accent) 0%, #d4b86a 100%)',
            border: 'none', color: '#030B18', fontSize: 14, fontWeight: 600,
            cursor: 'pointer', transition: 'all 200ms',
          }}
        >
          确认预约
        </button>
        <button
          onClick={onCancel}
          style={{
            padding: '12px 20px', borderRadius: 12,
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.08)',
            color: 'var(--text-secondary)', fontSize: 14,
            cursor: 'pointer', transition: 'all 200ms',
          }}
        >
          取消
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   主组件
   ═══════════════════════════════════════════ */

export default function Services() {
  const navigate = useNavigate();
  const [location, setLocation] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [agentSteps, setAgentSteps] = useState<AgentStep[]>(AGENT_PIPELINE_STEPS);
  const [businesses, setBusinesses] = useState<Business[]>(MOCK_BUSINESSES);
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const resultRef = useRef<HTMLDivElement>(null);

  // 滚动到顶部
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // 搜索结果滚动
  useEffect(() => {
    if (searched && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [searched]);

  /** 模拟 Agent 匹配流程 */
  const runAgentPipeline = useCallback(async () => {
    setIsSearching(true);
    setSearched(false);
    setConfirmed(false);
    setSelectedBusiness(null);
    setSelectedDate('');
    setSelectedTime('');
    setAgentSteps(AGENT_PIPELINE_STEPS.map(s => ({ ...s, status: 'idle' })));

    // 逐步执行 Agent 流程
    for (let i = 0; i < AGENT_PIPELINE_STEPS.length; i++) {
      setAgentSteps(prev => prev.map((s, idx) =>
        idx === i ? { ...s, status: 'running' } : s
      ));

      await new Promise(resolve => setTimeout(resolve, 600 + Math.random() * 400));

      setAgentSteps(prev => prev.map((s, idx) =>
        idx === i ? { ...s, status: 'done' } :
        idx === i + 1 ? { ...s, status: 'running' } : s
      ));
    }

    // 最后一步完成
    setAgentSteps(prev => prev.map(s => ({ ...s, status: 'done' })));
    setIsSearching(false);
    setSearched(true);
  }, []);

  /** 搜索处理 */
  const handleSearch = useCallback(() => {
    const hasQuery = searchQuery.trim() || selectedCategory || location.trim();
    if (!hasQuery) return;
    runAgentPipeline();
  }, [searchQuery, selectedCategory, location, runAgentPipeline]);

  /** 分类点击 */
  const handleCategoryClick = useCallback((id: string) => {
    setSelectedCategory(prev => prev === id ? '' : id);
    setSearched(false);
  }, []);

  /** 商家选择 */
  const handleBusinessSelect = useCallback((business: Business) => {
    setSelectedBusiness(business);
    setConfirmed(false);
  }, []);

  /** 确认预约 */
  const handleConfirm = useCallback(() => {
    setConfirmed(true);
    // 可扩展为跳转到预约详情页
    // navigate('/booking-confirm');
  }, []);

  /** 取消预约 */
  const handleCancel = useCallback(() => {
    setSelectedBusiness(null);
    setSelectedDate('');
    setSelectedTime('');
    setConfirmed(false);
  }, []);

  /** 键盘搜索 */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        padding: '28px 24px 60px',
        fontFamily: 'var(--font-sans)',
        color: 'var(--text-primary)',
      }}
    >
      {/* ── 页面标题区 ── */}
      <header
        style={{
          maxWidth: 780, margin: '0 auto 28px',
          animation: 'fadeUp 0.6s var(--ease) both',
        }}
      >
        <div style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', marginBottom: 8 }}>
          {getTimeOfDay()}
        </div>
        <h1 style={{ fontSize: 28, fontWeight: 300, letterSpacing: '-0.02em', margin: '0 0 6px' }}>
          本地服务
        </h1>
        <p style={{ fontSize: 14, color: 'var(--text-muted)', margin: 0, lineHeight: 1.6 }}>
          {getGreeting()}，AI Agent 为你智能匹配周边优质服务商家
        </p>
      </header>

      {/* ── 主内容区 ── */}
      <div style={{ maxWidth: 780, margin: '0 auto' }}>

        {/* ── 服务分类卡片 ── */}
        <section
          style={{
            animation: 'fadeUp 0.6s 0.05s var(--ease) both',
          }}
        >
          <h2 style={{ fontSize: 15, fontWeight: 400, color: 'var(--text-secondary)', margin: '0 0 14px' }}>
            服务类型
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
              gap: 10,
            }}
          >
            {SERVICE_CATEGORIES.map((cat, idx) => (
              <ServiceCategoryCard
                key={cat.id}
                category={cat}
                isSelected={selectedCategory === cat.id}
                onClick={handleCategoryClick}
                index={idx}
              />
            ))}
          </div>
        </section>

        {/* ── 搜索区域 ── */}
        <section
          style={{
            marginTop: 28,
            animation: 'fadeUp 0.6s 0.15s var(--ease) both',
          }}
        >
          <h2 style={{ fontSize: 15, fontWeight: 400, color: 'var(--text-secondary)', margin: '0 0 14px' }}>
            搜索服务
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', gap: 10 }}>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="📍 输入位置（如：朝阳区）"
                style={{
                  flex: 1, padding: '12px 16px', borderRadius: 12,
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  color: 'var(--text-primary)', fontSize: 14,
                  outline: 'none', fontFamily: 'var(--font-sans)',
                  transition: 'border-color 200ms',
                }}
                onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(232, 210, 138, 0.2)'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
                onKeyDown={handleKeyDown}
              />
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="搜索服务类型或商家名称..."
                style={{
                  flex: 1, padding: '12px 16px', borderRadius: 12,
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  color: 'var(--text-primary)', fontSize: 14,
                  outline: 'none', fontFamily: 'var(--font-sans)',
                  transition: 'border-color 200ms',
                }}
                onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(232, 210, 138, 0.2)'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
              />
              <button
                onClick={handleSearch}
                disabled={isSearching || (!searchQuery.trim() && !selectedCategory && !location.trim())}
                style={{
                  padding: '12px 24px', borderRadius: 12, border: 'none',
                  background: (searchQuery.trim() || selectedCategory || location.trim())
                    ? 'linear-gradient(135deg, var(--accent) 0%, #d4b86a 100%)'
                    : 'rgba(255,255,255,0.05)',
                  color: (searchQuery.trim() || selectedCategory || location.trim()) ? '#030B18' : 'var(--text-disabled)',
                  fontSize: 14, fontWeight: 500,
                  cursor: (searchQuery.trim() || selectedCategory || location.trim()) ? 'pointer' : 'default',
                  opacity: isSearching ? 0.6 : 1,
                  transition: 'all 200ms',
                  whiteSpace: 'nowrap',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                {isSearching ? (
                  <>
                    <span style={{ animation: 'gentlePulse 1s ease-in-out infinite' }}>⚡</span>
                    搜索中
                  </>
                ) : '搜索'}
              </button>
            </div>
          </div>
        </section>

        {/* ── Agent 工作状态 ── */}
        {isSearching && (
          <div style={{ marginTop: 20 }}>
            <AgentProgress steps={agentSteps} />
          </div>
        )}

        {/* ── 搜索结果区域 ── */}
        {searched && (
          <section
            ref={resultRef}
            style={{
              marginTop: 32,
              display: 'flex', flexDirection: 'column', gap: 24,
            }}
          >
            {/* 商家列表 */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <h2 style={{ fontSize: 16, fontWeight: 400, color: 'var(--text-secondary)', margin: 0 }}>
                  推荐商家
                </h2>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  找到 {businesses.length} 家匹配商家
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {businesses.map((biz) => (
                  <BusinessCard
                    key={biz.id}
                    business={biz}
                    isSelected={selectedBusiness?.id === biz.id}
                    onSelect={handleBusinessSelect}
                  />
                ))}
              </div>
            </div>

            {/* 预约时间选择器 */}
            {selectedBusiness && (
              <TimeSlotPicker
                business={selectedBusiness}
                selectedDate={selectedDate}
                selectedTime={selectedTime}
                onSelectDate={setSelectedDate}
                onSelectTime={setSelectedTime}
              />
            )}

            {/* 预约确认卡片 */}
            {selectedBusiness && selectedDate && selectedTime && !confirmed && (
              <BookingConfirmation
                booking={{ business: selectedBusiness, date: selectedDate, timeSlot: selectedTime }}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
              />
            )}

            {/* 预约成功提示 */}
            {confirmed && (
              <div
                style={{
                  padding: '20px', borderRadius: 16,
                  background: 'rgba(74, 222, 128, 0.06)',
                  border: '1px solid rgba(74, 222, 128, 0.2)',
                  textAlign: 'center',
                  animation: 'fadeUp 0.4s var(--ease) both',
                }}
              >
                <div style={{ fontSize: 32, marginBottom: 10 }}>✅</div>
                <div style={{ fontSize: 16, fontWeight: 500, color: '#4ade80', marginBottom: 6 }}>
                  预约成功
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>
                  {selectedBusiness.name} · {DATE_OPTIONS.find(d => d.value === selectedDate)?.label} {selectedTime}
                </div>
                <button
                  onClick={() => {
                    setConfirmed(false);
                    setSelectedBusiness(null);
                    setSelectedDate('');
                    setSelectedTime('');
                    setSearched(false);
                  }}
                  style={{
                    padding: '10px 20px', borderRadius: 12,
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: 'var(--text-secondary)', fontSize: 13,
                    cursor: 'pointer', transition: 'all 200ms',
                  }}
                >
                  继续预约其他服务
                </button>
              </div>
            )}
          </section>
        )}

        {/* ── 底部 Agent 信息 ── */}
        <section
          style={{
            marginTop: 48, paddingTop: 32,
            borderTop: '1px solid rgba(255,255,255,0.05)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <span style={{
              width: 6, height: 6, borderRadius: '50%',
              background: '#34d399', animation: 'gentlePulse 2s ease-in-out infinite',
            }} />
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              服务匹配 Agent 在线
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {AGENT_PIPELINE_STEPS.map((step) => (
              <span
                key={step.id}
                style={{
                  padding: '5px 12px', borderRadius: 20,
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.05)',
                  fontSize: 12, color: 'var(--text-muted)',
                }}
              >
                {step.icon} {step.label}
              </span>
            ))}
          </div>
        </section>
      </div>

      {/* ── 全局样式（动画） ── */}
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes gentlePulse {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
